/**
 * Export Utilities for Analyst Pack
 *
 * Client-side export functions for research reports:
 * - PDF: Generate via jsPDF or print-to-PDF fallback
 * - CSV: Extract data tables (sources, metrics, confidence scores)
 * - JSON: Full audit trail with all agent outputs
 * - Markdown: Clean formatted text for copy/paste
 */

import type { ReportData, TaskResult, AgentOutput } from '@/hooks/useResearchSession';
import type { ExportFormat, RejectedArgument, DataSourceTimestamp } from '@/types/researchModes';
import type { FinancialKillSheet, AnchoredSnapshot } from '@/components/verdict/FinancialAnalysisPanel';
import type { FinancialAgentProgress, MediatorCompilation } from '@/components/verdict/FinancialLogicTree';
import type {
  StrategicWorkstream, StrategicConflict, StressTestResult,
  DebateArgument, MediatorConsensus, GhostDeckSlide,
  StrategicDeliverable, CompetitorData, StrategicIntensity,
} from '@/hooks/useStrategicStream';

export interface ExportOptions {
  query: string;
  reportData: ReportData;
  timestamp?: string;
}

/** Strategic-specific export options */
export interface StrategicExportOptions {
  query: string;
  topic: string;
  intensity: StrategicIntensity;
  workstreams: StrategicWorkstream[];
  conflicts: StrategicConflict[];
  stressTestResults: StressTestResult[];
  debateArguments: DebateArgument[];
  debateRounds: number;
  mediatorConsensus?: MediatorConsensus;
  ghostDeck: GhostDeckSlide[];
  deliverable?: StrategicDeliverable;
  competitorData: CompetitorData[];
  startedAt?: string;
  completedAt?: string;
  timestamp?: string;
}

/** Financial-specific export options */
export interface FinancialExportOptions {
  query: string;
  ticker: string;
  killSheet: FinancialKillSheet;
  bullAgents: FinancialAgentProgress[];
  bearAgents: FinancialAgentProgress[];
  anchoredSnapshot?: AnchoredSnapshot;
  mediatorCompilation?: MediatorCompilation;
  timestamp?: string;
}

/**
 * Generate Markdown summary for copy/paste
 */
export function generateMarkdown(options: ExportOptions): string {
  const { query, reportData, timestamp = new Date().toISOString() } = options;
  
  let md = `# Research Report: ${query}\n\n`;
  md += `**Generated:** ${new Date(timestamp).toLocaleString()}\n`;
  md += `**Duration:** ${reportData.durationSeconds ? `${Math.round(reportData.durationSeconds)}s` : 'N/A'}\n`;
  if (reportData.confidenceScore !== undefined) {
    md += `**Confidence:** ${Math.round(reportData.confidenceScore * 100)}%\n`;
  }
  md += '\n';

  // Executive Summary
  md += `## Executive Summary\n\n${reportData.verdict}\n\n`;
  
  // Data Timestamps (Radical Transparency)
  if (reportData.dataTimestamps && reportData.dataTimestamps.length > 0) {
    md += `## Data Sources\n\n`;
    reportData.dataTimestamps.forEach((ts: DataSourceTimestamp) => {
      const delay = ts.delay ? ` (${ts.delay})` : '';
      md += `- **${ts.source}** - ${ts.dataType}${delay} - ${new Date(ts.timestamp).toLocaleString()}\n`;
    });
    md += '\n';
  }
  
  // Task Results
  if (reportData.taskResults && reportData.taskResults.length > 0) {
    md += `## Research Tasks\n\n`;
    reportData.taskResults.forEach((task: TaskResult, idx: number) => {
      md += `### ${idx + 1}. ${task.taskName}\n\n`;
      if (task.taskDescription) {
        md += `*${task.taskDescription}*\n\n`;
      }
      if (task.confidenceScore !== undefined) {
        md += `**Confidence:** ${Math.round(task.confidenceScore * 100)}%\n\n`;
      }
      if (task.aggregatedOutput) {
        md += `${task.aggregatedOutput}\n\n`;
      }

      // Per-task agent outputs (audit trail)
      const agentsWithOutput = task.agentOutputs.filter((a: AgentOutput) => a.output);
      if (agentsWithOutput.length > 0) {
        md += `#### Agent Outputs\n\n`;
        agentsWithOutput.forEach((agent: AgentOutput) => {
          const latency = agent.latencySeconds ? ` (${agent.latencySeconds.toFixed(1)}s)` : '';
          md += `**${agent.agentName}** (${agent.modelName})${latency}\n\n`;
          md += `${agent.output}\n\n`;
        });
      }

      // Per-task fact-check results
      if (task.factCheck) {
        const fc = task.factCheck;
        md += `#### Fact-Check: ${fc.isValidated ? 'Validated' : 'Issues Found'}`;
        if (fc.model) md += ` (${fc.model})`;
        md += `\n\n`;
        if (fc.keyFindings.length > 0) {
          md += `**Key Findings:**\n`;
          fc.keyFindings.forEach(f => { md += `- ${f}\n`; });
          md += '\n';
        }
        if (fc.discrepancies.length > 0) {
          md += `**Discrepancies:**\n`;
          fc.discrepancies.forEach(d => { md += `- ${d}\n`; });
          md += '\n';
        }
      }

      // Per-task debate results
      if (task.debate) {
        const db = task.debate;
        md += `#### Debate (${db.roundsCompleted} rounds, ${Math.round(db.agreementScore * 100)}% agreement)`;
        if (db.mediatorModel) md += ` — Mediator: ${db.mediatorModel}`;
        md += `\n\n`;
        if (db.finalConsensus) {
          md += `**Consensus:** ${db.finalConsensus}\n\n`;
        }
        if (db.resolvedDiscrepancies.length > 0) {
          md += `**Resolved:**\n`;
          db.resolvedDiscrepancies.forEach(d => { md += `- ${d}\n`; });
          md += '\n';
        }
      }
    });
  }

  // Rejected Arguments (Kill Sheet)
  if (reportData.rejectedArguments && reportData.rejectedArguments.length > 0) {
    md += `## Rejected Arguments (Kill Sheet)\n\n`;
    reportData.rejectedArguments.forEach((ra: RejectedArgument) => {
      md += `### ${ra.arguedBy} (${ra.agentModel})\n`;
      md += `- **Argument:** ${ra.argumentSummary}\n`;
      md += `- **Rejection Reason:** ${ra.rejectionReason.replace('REJECTED_', '')}\n`;
      md += `- **Detail:** ${ra.rejectionDetail}\n`;
      if (ra.counterEvidence) {
        md += `- **Counter-Evidence:** ${ra.counterEvidence}\n`;
      }
      md += '\n';
    });
  }
  
  // Sources
  if (reportData.sources && reportData.sources.length > 0) {
    md += `## Sources\n\n`;
    reportData.sources.forEach((source) => {
      if (typeof source === 'string') {
        md += `- ${source}\n`;
      } else {
        md += source.title ? `- [${source.title}](${source.url})\n` : `- ${source.url}\n`;
      }
    });
  }
  
  md += `\n---\n*Generated by Vettis AI Research Platform*\n`;
  return md;
}

/**
 * Generate CSV with data tables
 */
export function generateCSV(options: ExportOptions): string {
  const { query, reportData, timestamp = new Date().toISOString() } = options;

  let csv = '';

  // Report summary
  csv += 'REPORT SUMMARY\n';
  csv += 'Field,Value\n';
  csv += `"Query","${csvSafe(query)}"\n`;
  csv += `"Generated","${new Date(timestamp).toLocaleString()}"\n`;
  csv += `"Duration","${reportData.durationSeconds ? Math.round(reportData.durationSeconds) + 's' : 'N/A'}"\n`;
  csv += `"Overall Confidence","${reportData.confidenceScore !== undefined ? Math.round(reportData.confidenceScore * 100) + '%' : 'N/A'}"\n`;
  csv += `"Total Sources","${reportData.sources.length}"\n`;
  csv += `"Total Tasks","${reportData.taskResults.length}"\n`;
  if (reportData.totalCreditsUsed) {
    csv += `"Credits Used","${reportData.totalCreditsUsed}"\n`;
  }
  csv += '\n';

  // Verdict
  csv += 'EXECUTIVE SUMMARY\n';
  csv += 'Verdict\n';
  csv += `"${csvSafe(reportData.verdict)}"\n\n`;

  // Tasks table
  csv += 'TASK RESULTS\n';
  csv += 'Task Name,Description,Confidence,Debate Occurred,Aggregated Output\n';
  reportData.taskResults.forEach((task: TaskResult) => {
    const conf = task.confidenceScore !== undefined ? Math.round(task.confidenceScore * 100) + '%' : 'N/A';
    csv += `"${csvSafe(task.taskName)}","${csvSafe(task.taskDescription)}","${conf}",${task.debateOccurred || false},"${csvSafe(task.aggregatedOutput)}"\n`;
  });
  csv += '\n';

  // Agent outputs table
  const allAgentOutputs: { task: string; agent: string; model: string; provider: string; latency: string; sources: number; success: boolean; output: string }[] = [];
  reportData.taskResults.forEach((task: TaskResult) => {
    task.agentOutputs.forEach((agent: AgentOutput) => {
      if (agent.output) {
        allAgentOutputs.push({
          task: task.taskName,
          agent: agent.agentName,
          model: agent.modelName,
          provider: agent.provider || '',
          latency: agent.latencySeconds ? agent.latencySeconds.toFixed(1) : 'N/A',
          sources: agent.webSourcesUsed || 0,
          success: agent.success !== false,
          output: agent.output,
        });
      }
    });
  });
  if (allAgentOutputs.length > 0) {
    csv += 'AGENT OUTPUTS\n';
    csv += 'Task,Agent,Model,Provider,Latency (s),Web Sources,Success,Output\n';
    allAgentOutputs.forEach(a => {
      csv += `"${csvSafe(a.task)}","${csvSafe(a.agent)}","${csvSafe(a.model)}","${csvSafe(a.provider)}",${a.latency},${a.sources},${a.success},"${csvSafe(a.output)}"\n`;
    });
    csv += '\n';
  }

  // Fact-check results
  const tasksWithFactCheck = reportData.taskResults.filter(t => t.factCheck);
  if (tasksWithFactCheck.length > 0) {
    csv += 'FACT-CHECK RESULTS\n';
    csv += 'Task,Validated,Model,Key Findings,Discrepancies\n';
    tasksWithFactCheck.forEach(task => {
      const fc = task.factCheck!;
      csv += `"${csvSafe(task.taskName)}",${fc.isValidated},"${csvSafe(fc.model)}","${csvSafe(fc.keyFindings.join('; '))}","${csvSafe(fc.discrepancies.join('; '))}"\n`;
    });
    csv += '\n';
  }

  // Debate results
  const tasksWithDebate = reportData.taskResults.filter(t => t.debate);
  if (tasksWithDebate.length > 0) {
    csv += 'DEBATE RESULTS\n';
    csv += 'Task,Rounds,Agreement Score,Mediator,Final Consensus,Resolved Discrepancies\n';
    tasksWithDebate.forEach(task => {
      const db = task.debate!;
      csv += `"${csvSafe(task.taskName)}",${db.roundsCompleted},${Math.round(db.agreementScore * 100)}%,"${csvSafe(db.mediatorModel)}","${csvSafe(db.finalConsensus)}","${csvSafe(db.resolvedDiscrepancies.join('; '))}"\n`;
    });
    csv += '\n';
  }

  // Sources table
  csv += 'SOURCES\n';
  csv += 'URL,Title\n';
  reportData.sources.forEach((source) => {
    const url = typeof source === 'string' ? source : source.url;
    const title = typeof source === 'string' ? '' : (source.title || '');
    csv += `"${csvSafe(url)}","${csvSafe(title)}"\n`;
  });
  csv += '\n';

  // Data timestamps table
  if (reportData.dataTimestamps && reportData.dataTimestamps.length > 0) {
    csv += 'DATA TIMESTAMPS\n';
    csv += 'Source,Data Type,Timestamp,Delay,Real-time\n';
    reportData.dataTimestamps.forEach((ts: DataSourceTimestamp) => {
      csv += `"${ts.source}","${ts.dataType}","${ts.timestamp}","${ts.delay || ''}",${ts.isRealtime}\n`;
    });
    csv += '\n';
  }

  // Rejected arguments table (Kill Sheet)
  if (reportData.rejectedArguments && reportData.rejectedArguments.length > 0) {
    csv += 'REJECTED ARGUMENTS (KILL SHEET)\n';
    csv += 'Agent,Model,Argument,Rejection Reason,Detail,Counter-Evidence\n';
    reportData.rejectedArguments.forEach((ra: RejectedArgument) => {
      csv += `"${csvSafe(ra.arguedBy)}","${csvSafe(ra.agentModel)}","${csvSafe(ra.argumentSummary)}","${ra.rejectionReason}","${csvSafe(ra.rejectionDetail)}","${csvSafe(ra.counterEvidence)}"\n`;
    });
  }

  return csv;
}

/**
 * Generate JSON audit trail
 */
export function generateJSON(options: ExportOptions): string {
  const { query, reportData, timestamp = new Date().toISOString() } = options;

  const auditTrail = {
    meta: {
      type: 'general_research',
      version: '1.0',
      query,
      generatedAt: timestamp,
      durationSeconds: reportData.durationSeconds || null,
      confidenceScore: reportData.confidenceScore ?? null,
      totalCreditsUsed: reportData.totalCreditsUsed || null,
      taskCount: reportData.taskResults.length,
      sourceCount: reportData.sources.length,
    },
    verdict: reportData.verdict,
    taskResults: reportData.taskResults.map(task => ({
      taskName: task.taskName,
      taskDescription: task.taskDescription,
      confidenceScore: task.confidenceScore ?? null,
      debateOccurred: task.debateOccurred || false,
      aggregatedOutput: task.aggregatedOutput,
      agentOutputs: task.agentOutputs.map(agent => ({
        agentName: agent.agentName,
        modelName: agent.modelName,
        provider: agent.provider || null,
        output: agent.output || null,
        latencySeconds: agent.latencySeconds || null,
        webSourcesUsed: agent.webSourcesUsed || 0,
        success: agent.success !== false,
      })),
      webSources: task.webSources || [],
      factCheck: task.factCheck || null,
      debate: task.debate || null,
    })),
    dataTimestamps: reportData.dataTimestamps || [],
    rejectedArguments: reportData.rejectedArguments || [],
    sources: reportData.sources,
  };

  return JSON.stringify(auditTrail, null, 2);
}

/**
 * Download content as a file
 */
function downloadFile(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Copy text to clipboard
 */
async function copyToClipboard(text: string): Promise<void> {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
  } else {
    // Fallback for non-secure contexts
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  }
}

/**
 * Generate PDF using print dialog (fallback without jsPDF)
 */
function generatePDFPrint(options: ExportOptions): void {
  const { query, reportData, timestamp = new Date().toISOString() } = options;

  // Build HTML content for PDF
  const html = `
<!DOCTYPE html>
<html>
<head>
  <title>Research Report - ${query}</title>
  <style>
    body { font-family: system-ui, sans-serif; max-width: 800px; margin: 0 auto; padding: 40px; color: #1a1a1a; }
    h1 { font-size: 24px; border-bottom: 2px solid #d4af37; padding-bottom: 12px; }
    h2 { font-size: 18px; color: #444; margin-top: 32px; }
    h3 { font-size: 14px; color: #666; }
    h4 { font-size: 13px; color: #888; margin-top: 16px; }
    .meta { color: #666; font-size: 12px; margin-bottom: 24px; }
    .verdict { background: #f9f9f9; padding: 20px; border-radius: 8px; border-left: 4px solid #d4af37; }
    .task { margin: 16px 0; padding: 16px; background: #fafafa; border-radius: 8px; page-break-inside: avoid; }
    .agent { background: #f5f5f5; padding: 12px; border-radius: 6px; margin: 8px 0; border-left: 3px solid #888; }
    .agent-header { font-weight: 600; font-size: 13px; margin-bottom: 4px; }
    .fact-check { background: #f0fdf4; padding: 12px; border-radius: 6px; margin: 8px 0; border-left: 3px solid #22c55e; }
    .fact-check.issues { background: #fef2f2; border-left-color: #ef4444; }
    .debate-result { background: #faf5ff; padding: 12px; border-radius: 6px; margin: 8px 0; border-left: 3px solid #a855f7; }
    .sources { font-size: 12px; color: #666; }
    .sources a { color: #0066cc; }
    .kill-sheet { background: #fff5f5; padding: 16px; border-radius: 8px; margin: 16px 0; }
    .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; font-size: 11px; color: #999; }
    @media print { body { padding: 20px; } .task, .agent { page-break-inside: avoid; } }
  </style>
</head>
<body>
  <h1>Research Report</h1>
  <div class="meta">
    <strong>Query:</strong> ${query}<br>
    <strong>Generated:</strong> ${new Date(timestamp).toLocaleString()}<br>
    <strong>Duration:</strong> ${reportData.durationSeconds ? Math.round(reportData.durationSeconds) + 's' : 'N/A'}${reportData.confidenceScore !== undefined ? `<br><strong>Confidence:</strong> ${Math.round(reportData.confidenceScore * 100)}%` : ''}${reportData.totalCreditsUsed ? `<br><strong>Credits:</strong> ${reportData.totalCreditsUsed}` : ''}
  </div>

  <h2>Executive Summary</h2>
  <div class="verdict">${reportData.verdict.replace(/\n/g, '<br>')}</div>

  ${reportData.taskResults.length > 0 ? `
  <h2>Research Tasks</h2>
  ${reportData.taskResults.map((task: TaskResult, idx: number) => {
    const agentsWithOutput = task.agentOutputs.filter((a: AgentOutput) => a.output);
    return `
    <div class="task">
      <h3>${idx + 1}. ${task.taskName}${task.confidenceScore !== undefined ? ` <span style="color: ${task.confidenceScore >= 0.7 ? '#22c55e' : task.confidenceScore >= 0.5 ? '#f59e0b' : '#ef4444'}; font-size: 12px;">(${Math.round(task.confidenceScore * 100)}% confidence)</span>` : ''}${task.debateOccurred ? ' <span style="color: #a855f7; font-size: 11px;">[debated]</span>' : ''}</h3>
      ${task.taskDescription ? `<p style="color: #666; font-size: 13px; margin-bottom: 8px;"><em>${task.taskDescription}</em></p>` : ''}
      ${task.aggregatedOutput ? `<div style="font-size: 14px;">${task.aggregatedOutput.replace(/\n/g, '<br>')}</div>` : ''}

      ${agentsWithOutput.length > 0 ? `
        <h4>Agent Outputs (${agentsWithOutput.length})</h4>
        ${agentsWithOutput.map((agent: AgentOutput) => `
          <div class="agent">
            <div class="agent-header">${agent.agentName} <span style="color:#888;font-weight:normal">(${agent.modelName})</span>${agent.latencySeconds ? ` <span style="color:#999;font-size:11px">${agent.latencySeconds.toFixed(1)}s</span>` : ''}${agent.webSourcesUsed ? ` <span style="color:#999;font-size:11px">${agent.webSourcesUsed} sources</span>` : ''}</div>
            <div style="font-size:13px">${agent.output.replace(/\n/g, '<br>')}</div>
          </div>
        `).join('')}
      ` : ''}

      ${task.factCheck ? `
        <div class="fact-check${task.factCheck.isValidated ? '' : ' issues'}">
          <strong>Fact-Check: ${task.factCheck.isValidated ? 'Validated' : 'Issues Found'}</strong>${task.factCheck.model ? ` <span style="color:#888;font-size:11px">(${task.factCheck.model})</span>` : ''}
          ${task.factCheck.keyFindings.length > 0 ? `<br><small><strong>Findings:</strong> ${task.factCheck.keyFindings.join('; ')}</small>` : ''}
          ${task.factCheck.discrepancies.length > 0 ? `<br><small><strong>Discrepancies:</strong> ${task.factCheck.discrepancies.join('; ')}</small>` : ''}
        </div>
      ` : ''}

      ${task.debate ? `
        <div class="debate-result">
          <strong>Debate: ${task.debate.roundsCompleted} rounds, ${Math.round(task.debate.agreementScore * 100)}% agreement</strong>${task.debate.mediatorModel ? ` <span style="color:#888;font-size:11px">(Mediator: ${task.debate.mediatorModel})</span>` : ''}
          ${task.debate.finalConsensus ? `<br><small>${task.debate.finalConsensus}</small>` : ''}
          ${task.debate.resolvedDiscrepancies.length > 0 ? `<br><small><strong>Resolved:</strong> ${task.debate.resolvedDiscrepancies.join('; ')}</small>` : ''}
        </div>
      ` : ''}
    </div>
  `}).join('')}
  ` : ''}

  ${reportData.rejectedArguments && reportData.rejectedArguments.length > 0 ? `
  <h2>Rejected Arguments (Kill Sheet)</h2>
  ${reportData.rejectedArguments.map((ra: RejectedArgument) => `
    <div class="kill-sheet">
      <strong>${ra.arguedBy}</strong> (${ra.agentModel})<br>
      <em>${ra.argumentSummary}</em><br>
      <small>Rejection: ${ra.rejectionReason.replace('REJECTED_', '')} - ${ra.rejectionDetail}</small>
      ${ra.counterEvidence ? `<br><small><strong>Counter-Evidence:</strong> ${ra.counterEvidence}</small>` : ''}
    </div>
  `).join('')}
  ` : ''}

  ${reportData.sources.length > 0 ? `
  <h2>Sources</h2>
  <div class="sources">
    ${reportData.sources.map((s) => {
      const url = typeof s === 'string' ? s : s.url;
      const title = typeof s === 'string' ? url : (s.title || url);
      return `<a href="${url}" target="_blank">${title}</a><br>`;
    }).join('')}
  </div>
  ` : ''}

  <div class="footer">Generated by Vettis AI Research Platform</div>
</body>
</html>`;

  // Open in new window for print
  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.print();
  }
}

// ============================================================================
// FINANCIAL DEEP DIVE EXPORT FUNCTIONS
// ============================================================================

/**
 * Generate Markdown for Financial Deep Dive (full audit trail)
 */
export function generateFinancialMarkdown(options: FinancialExportOptions): string {
  const { query, ticker, killSheet, bullAgents, bearAgents, anchoredSnapshot, mediatorCompilation, timestamp = new Date().toISOString() } = options;

  let md = `# Financial Deep Dive: ${ticker}\n\n`;
  md += `**Query:** ${query}\n`;
  md += `**Generated:** ${new Date(timestamp).toLocaleString()}\n`;
  md += `**Analysis Date:** ${killSheet.analysis_date}\n`;
  md += `**Research Depth:** ${killSheet.research_depth}\n`;
  md += `**Total Agents:** ${killSheet.total_agents}\n`;
  md += `**Debate Rounds:** ${killSheet.debate_rounds}\n\n`;

  // Anchored Data Snapshot
  if (anchoredSnapshot) {
    md += `## Anchored Data Snapshot\n\n`;
    md += `| Metric | Value |\n`;
    md += `|--------|-------|\n`;
    md += `| Price | ${anchoredSnapshot.priceFormatted || anchoredSnapshot.price || 'N/A'} |\n`;
    md += `| Market Cap | ${anchoredSnapshot.marketCapFormatted || 'N/A'} |\n`;
    md += `| P/E (TTM) | ${anchoredSnapshot.peFormatted || anchoredSnapshot.peTTM || 'N/A'} |\n`;
    md += `| EPS (TTM) | ${anchoredSnapshot.epsFormatted || anchoredSnapshot.epsTTM || 'N/A'} |\n`;
    md += `| Change | ${anchoredSnapshot.changePercentFormatted || 'N/A'} |\n`;
    md += `| As Of | ${anchoredSnapshot.asOf || 'N/A'} |\n\n`;
  }

  // Kill Sheet Verdict
  md += `## Kill Sheet Verdict\n\n`;
  md += `**Winner:** ${killSheet.winning_side.toUpperCase()} CASE\n`;
  md += `**Confidence:** ${Math.round(killSheet.confidence * 100)}%\n\n`;

  if (killSheet.bull_intensity !== undefined) {
    md += `**Bull Intensity:** ${killSheet.bull_intensity}%\n`;
  }
  if (killSheet.bear_risk !== undefined) {
    md += `**Bear Risk:** ${killSheet.bear_risk}%\n`;
  }
  md += '\n';

  // Bull Thesis
  md += `### Bull Thesis\n\n${killSheet.bull_thesis}\n\n`;

  // Bear Thesis
  md += `### Bear Thesis\n\n${killSheet.bear_thesis}\n\n`;

  // Kill Sheet Reason
  if (killSheet.kill_sheet_reason) {
    md += `### Kill Sheet Reason\n\n${killSheet.kill_sheet_reason}\n\n`;
  }

  // Caveats
  if (killSheet.caveats) {
    md += `### Caveats\n\n${killSheet.caveats}\n\n`;
  }

  // Agent Outputs - Bull Team
  md += `## Bull Team Agents (${bullAgents.length})\n\n`;
  bullAgents.forEach((agent, idx) => {
    md += `### ${idx + 1}. ${agent.roleName} (${agent.model})\n\n`;
    if (agent.latency) {
      md += `*Latency: ${(agent.latency / 1000).toFixed(1)}s*\n\n`;
    }
    if (agent.output) {
      md += `${agent.output}\n\n`;
    }
    // Agent Citations
    if (agent.citations && agent.citations.length > 0) {
      md += `**Sources (${agent.citations.length}):**\n`;
      agent.citations.forEach((c) => {
        const time = c.published_at ? ` (${new Date(c.published_at).toLocaleDateString()})` : '';
        const url = c.url ? ` - [Link](${c.url})` : '';
        md += `- [${c.id}] ${c.source_type}: ${c.title}${time}${url}\n`;
      });
      md += '\n';
    }
  });

  // Agent Outputs - Bear Team
  md += `## Bear Team Agents (${bearAgents.length})\n\n`;
  bearAgents.forEach((agent, idx) => {
    md += `### ${idx + 1}. ${agent.roleName} (${agent.model})\n\n`;
    if (agent.latency) {
      md += `*Latency: ${(agent.latency / 1000).toFixed(1)}s*\n\n`;
    }
    if (agent.output) {
      md += `${agent.output}\n\n`;
    }
    // Agent Citations
    if (agent.citations && agent.citations.length > 0) {
      md += `**Sources (${agent.citations.length}):**\n`;
      agent.citations.forEach((c) => {
        const time = c.published_at ? ` (${new Date(c.published_at).toLocaleDateString()})` : '';
        const url = c.url ? ` - [Link](${c.url})` : '';
        md += `- [${c.id}] ${c.source_type}: ${c.title}${time}${url}\n`;
      });
      md += '\n';
    }
  });

  // Mediator Compilation
  if (mediatorCompilation) {
    md += `## Mediator Compilation\n\n`;
    md += `**Bull Intensity:** ${mediatorCompilation.bullIntensity}%\n`;
    md += `**Bear Intensity:** ${mediatorCompilation.bearIntensity}%\n\n`;
    if (mediatorCompilation.verdict) {
      md += `**Verdict:** ${mediatorCompilation.verdict}\n\n`;
    }
  }

  // Rejected Arguments
  if (killSheet.rejected_arguments && killSheet.rejected_arguments.length > 0) {
    md += `## Rejected Arguments (${killSheet.rejected_arguments.length})\n\n`;
    killSheet.rejected_arguments.forEach((arg, idx) => {
      md += `### ${idx + 1}. ${arg.agent_role} (${arg.side.toUpperCase()})\n`;
      md += `- **Argument:** ${arg.argument_summary}\n`;
      md += `- **Rejection Reason:** ${arg.rejection_reason}\n`;
      md += `- **Detail:** ${arg.rejection_detail}\n`;
      if (arg.counter_evidence) {
        md += `- **Counter-Evidence:** ${arg.counter_evidence}\n`;
      }
      if (arg.sources_cited && arg.sources_cited.length > 0) {
        md += `- **Sources:** ${arg.sources_cited.join(', ')}\n`;
      }
      md += '\n';
    });
  }

  // Data Sources (Audit Trail)
  md += `## Data Sources (Audit Trail)\n\n`;
  if (killSheet.data_sources && Object.keys(killSheet.data_sources).length > 0) {
    Object.entries(killSheet.data_sources).forEach(([key, value]) => {
      md += `- **${key.toUpperCase()}:** ${value}\n`;
    });
  }

  md += `\n---\n*Generated by Vettis AI Financial Deep Dive*\n`;
  return md;
}

/**
 * Generate JSON audit trail for Financial Deep Dive
 */
export function generateFinancialJSON(options: FinancialExportOptions): string {
  const { query, ticker, killSheet, bullAgents, bearAgents, anchoredSnapshot, mediatorCompilation, timestamp = new Date().toISOString() } = options;

  const auditTrail = {
    meta: {
      type: 'financial_deep_dive',
      version: '1.0',
      query,
      ticker,
      generatedAt: timestamp,
      analysisDate: killSheet.analysis_date,
      researchDepth: killSheet.research_depth,
      totalAgents: killSheet.total_agents,
      debateRounds: killSheet.debate_rounds,
    },
    anchoredSnapshot: anchoredSnapshot || null,
    killSheet: {
      winningSide: killSheet.winning_side,
      confidence: killSheet.confidence,
      bullIntensity: killSheet.bull_intensity,
      bearRisk: killSheet.bear_risk,
      bullThesis: killSheet.bull_thesis,
      bearThesis: killSheet.bear_thesis,
      killSheetReason: killSheet.kill_sheet_reason,
      caveats: killSheet.caveats,
    },
    bullAgents: bullAgents.map(agent => ({
      role: agent.roleName,
      roleId: agent.roleId,
      model: agent.model,
      provider: agent.provider,
      status: agent.status,
      latencyMs: agent.latency,
      citationCount: agent.citationCount,
      output: agent.output,
      citations: agent.citations || [],
    })),
    bearAgents: bearAgents.map(agent => ({
      role: agent.roleName,
      roleId: agent.roleId,
      model: agent.model,
      provider: agent.provider,
      status: agent.status,
      latencyMs: agent.latency,
      citationCount: agent.citationCount,
      output: agent.output,
      citations: agent.citations || [],
    })),
    mediatorCompilation: mediatorCompilation || null,
    rejectedArguments: killSheet.rejected_arguments || [],
    dataSources: killSheet.data_sources || {},
  };

  return JSON.stringify(auditTrail, null, 2);
}

/**
 * Generate CSV for Financial Deep Dive
 */
export function generateFinancialCSV(options: FinancialExportOptions): string {
  const { ticker, killSheet, bullAgents, bearAgents, anchoredSnapshot } = options;

  let csv = '';

  // Summary
  csv += 'FINANCIAL DEEP DIVE SUMMARY\n';
  csv += 'Ticker,Winner,Confidence,Bull Intensity,Bear Risk,Analysis Date\n';
  csv += `"${ticker}","${killSheet.winning_side}",${Math.round(killSheet.confidence * 100)}%,${killSheet.bull_intensity || 'N/A'}%,${killSheet.bear_risk || 'N/A'}%,"${killSheet.analysis_date}"\n\n`;

  // Anchored Snapshot
  if (anchoredSnapshot) {
    csv += 'ANCHORED DATA SNAPSHOT\n';
    csv += 'Metric,Value\n';
    csv += `"Price","${anchoredSnapshot.priceFormatted || anchoredSnapshot.price || 'N/A'}"\n`;
    csv += `"Market Cap","${anchoredSnapshot.marketCapFormatted || 'N/A'}"\n`;
    csv += `"P/E (TTM)","${anchoredSnapshot.peFormatted || anchoredSnapshot.peTTM || 'N/A'}"\n`;
    csv += `"EPS (TTM)","${anchoredSnapshot.epsFormatted || anchoredSnapshot.epsTTM || 'N/A'}"\n`;
    csv += `"Change","${anchoredSnapshot.changePercentFormatted || 'N/A'}"\n`;
    csv += `"As Of","${anchoredSnapshot.asOf || 'N/A'}"\n\n`;
  }

  // Agent outputs
  csv += 'AGENT OUTPUTS\n';
  csv += 'Side,Role,Model,Latency (s),Citations,Status,Output\n';
  [...bullAgents, ...bearAgents].forEach((agent) => {
    const side = agent.side.toUpperCase();
    const latency = agent.latency ? (agent.latency / 1000).toFixed(1) : 'N/A';
    const output = (agent.output || '').replace(/"/g, '""').replace(/\n/g, ' ');
    csv += `"${side}","${agent.roleName}","${agent.model}",${latency},${agent.citationCount || 0},"${agent.status}","${output}"\n`;
  });
  csv += '\n';

  // All Citations
  csv += 'ALL CITATIONS (AUDIT TRAIL)\n';
  csv += 'Agent,Side,Citation ID,Source Type,Title,URL,Published At\n';
  [...bullAgents, ...bearAgents].forEach((agent) => {
    (agent.citations || []).forEach((c) => {
      const url = c.url ? `"${c.url}"` : '""';
      const pubAt = c.published_at || '';
      csv += `"${agent.roleName}","${agent.side}","${c.id}","${c.source_type}","${c.title.replace(/"/g, '""')}",${url},"${pubAt}"\n`;
    });
  });
  csv += '\n';

  // Rejected Arguments
  if (killSheet.rejected_arguments && killSheet.rejected_arguments.length > 0) {
    csv += 'REJECTED ARGUMENTS\n';
    csv += 'Side,Agent,Model,Argument,Rejection Reason,Detail,Counter-Evidence\n';
    killSheet.rejected_arguments.forEach((arg) => {
      const summary = arg.argument_summary.replace(/"/g, '""');
      const detail = arg.rejection_detail.replace(/"/g, '""');
      const counter = (arg.counter_evidence || '').replace(/"/g, '""');
      csv += `"${arg.side}","${arg.agent_role}","${arg.agent_model}","${summary}","${arg.rejection_reason}","${detail}","${counter}"\n`;
    });
    csv += '\n';
  }

  // Data Sources
  csv += 'DATA SOURCES (TIMESTAMPS)\n';
  csv += 'Source,Timestamp\n';
  Object.entries(killSheet.data_sources || {}).forEach(([key, value]) => {
    csv += `"${key}","${value}"\n`;
  });

  return csv;
}

/**
 * Generate PDF (print) for Financial Deep Dive
 */
function generateFinancialPDFPrint(options: FinancialExportOptions): void {
  const { query, ticker, killSheet, bullAgents, bearAgents, anchoredSnapshot, timestamp = new Date().toISOString() } = options;

  const isBullWin = killSheet.winning_side === 'bull';
  const winColor = isBullWin ? '#22c55e' : '#ef4444';

  const html = `
<!DOCTYPE html>
<html>
<head>
  <title>Financial Deep Dive - ${ticker}</title>
  <style>
    body { font-family: system-ui, sans-serif; max-width: 900px; margin: 0 auto; padding: 40px; color: #1a1a1a; }
    h1 { font-size: 28px; border-bottom: 3px solid ${winColor}; padding-bottom: 12px; }
    h2 { font-size: 18px; color: #444; margin-top: 32px; border-bottom: 1px solid #ddd; padding-bottom: 8px; }
    h3 { font-size: 14px; color: #666; }
    .meta { color: #666; font-size: 12px; margin-bottom: 24px; }
    .verdict { background: ${isBullWin ? '#f0fdf4' : '#fef2f2'}; padding: 20px; border-radius: 8px; border-left: 4px solid ${winColor}; margin: 20px 0; }
    .verdict-header { color: ${winColor}; font-weight: bold; font-size: 18px; margin-bottom: 12px; }
    .snapshot { background: #f9f9f9; padding: 16px; border-radius: 8px; margin: 16px 0; }
    .snapshot table { width: 100%; border-collapse: collapse; }
    .snapshot td { padding: 8px; border-bottom: 1px solid #eee; }
    .snapshot td:first-child { font-weight: 500; width: 40%; }
    .agent { background: #fafafa; padding: 16px; border-radius: 8px; margin: 12px 0; border-left: 3px solid #888; }
    .agent.bull { border-left-color: #22c55e; }
    .agent.bear { border-left-color: #ef4444; }
    .agent-header { font-weight: 600; margin-bottom: 8px; }
    .sources { font-size: 11px; color: #666; margin-top: 8px; padding-top: 8px; border-top: 1px dashed #ddd; }
    .rejected { background: #fef2f2; padding: 12px; border-radius: 6px; margin: 8px 0; }
    .data-sources { background: #f0f9ff; padding: 12px; border-radius: 6px; margin-top: 20px; }
    .data-sources .item { display: inline-block; margin: 4px 8px 4px 0; padding: 4px 8px; background: #fff; border-radius: 4px; font-size: 11px; }
    .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; font-size: 11px; color: #999; }
    @media print { body { padding: 20px; } }
  </style>
</head>
<body>
  <h1>Financial Deep Dive: ${ticker}</h1>
  <div class="meta">
    <strong>Query:</strong> ${query}<br>
    <strong>Generated:</strong> ${new Date(timestamp).toLocaleString()}<br>
    <strong>Research Depth:</strong> ${killSheet.research_depth} |
    <strong>Agents:</strong> ${killSheet.total_agents} |
    <strong>Debate Rounds:</strong> ${killSheet.debate_rounds}
  </div>

  ${anchoredSnapshot ? `
  <div class="snapshot">
    <h3 style="margin-top:0">Anchored Data Snapshot</h3>
    <table>
      <tr><td>Price</td><td>${anchoredSnapshot.priceFormatted || anchoredSnapshot.price || 'N/A'}</td></tr>
      <tr><td>Market Cap</td><td>${anchoredSnapshot.marketCapFormatted || 'N/A'}</td></tr>
      <tr><td>P/E (TTM)</td><td>${anchoredSnapshot.peFormatted || anchoredSnapshot.peTTM || 'N/A'}</td></tr>
      <tr><td>EPS (TTM)</td><td>${anchoredSnapshot.epsFormatted || anchoredSnapshot.epsTTM || 'N/A'}</td></tr>
      <tr><td>Change</td><td>${anchoredSnapshot.changePercentFormatted || 'N/A'}</td></tr>
      <tr><td>As Of</td><td>${anchoredSnapshot.asOf || 'N/A'}</td></tr>
    </table>
  </div>
  ` : ''}

  <div class="verdict">
    <div class="verdict-header">${isBullWin ? 'BULL' : 'BEAR'} CASE WINS (${Math.round(killSheet.confidence * 100)}% confidence)</div>
    <p>${isBullWin ? killSheet.bull_thesis : killSheet.bear_thesis}</p>
    ${killSheet.kill_sheet_reason ? `<p><strong>Reason:</strong> ${killSheet.kill_sheet_reason}</p>` : ''}
    ${killSheet.caveats ? `<p><strong>Caveats:</strong> ${killSheet.caveats}</p>` : ''}
  </div>

  <h2>Bull Team Agents</h2>
  ${bullAgents.map(agent => `
    <div class="agent bull">
      <div class="agent-header">${agent.roleName} <span style="color:#888;font-weight:normal">(${agent.model})</span></div>
      <div style="font-size:13px">${(agent.output || 'No output').replace(/\n/g, '<br>')}</div>
      ${agent.citations && agent.citations.length > 0 ? `
        <div class="sources">
          <strong>Sources:</strong> ${agent.citations.map(c => `[${c.id}] ${c.source_type}: ${c.title}`).join(' | ')}
        </div>
      ` : ''}
    </div>
  `).join('')}

  <h2>Bear Team Agents</h2>
  ${bearAgents.map(agent => `
    <div class="agent bear">
      <div class="agent-header">${agent.roleName} <span style="color:#888;font-weight:normal">(${agent.model})</span></div>
      <div style="font-size:13px">${(agent.output || 'No output').replace(/\n/g, '<br>')}</div>
      ${agent.citations && agent.citations.length > 0 ? `
        <div class="sources">
          <strong>Sources:</strong> ${agent.citations.map(c => `[${c.id}] ${c.source_type}: ${c.title}`).join(' | ')}
        </div>
      ` : ''}
    </div>
  `).join('')}

  ${killSheet.rejected_arguments && killSheet.rejected_arguments.length > 0 ? `
  <h2>Rejected Arguments (${killSheet.rejected_arguments.length})</h2>
  ${killSheet.rejected_arguments.map(arg => `
    <div class="rejected">
      <strong>${arg.agent_role}</strong> (${arg.side.toUpperCase()})<br>
      <em>${arg.argument_summary}</em><br>
      <small>Rejected: ${arg.rejection_reason} - ${arg.rejection_detail}</small>
    </div>
  `).join('')}
  ` : ''}

  <div class="data-sources">
    <strong>Data Sources (Audit Trail):</strong><br>
    ${Object.entries(killSheet.data_sources || {}).map(([key, value]) => `
      <span class="item"><strong>${key.toUpperCase()}:</strong> ${value}</span>
    `).join('')}
  </div>

  <div class="footer">Generated by Vettis AI Financial Deep Dive Platform</div>
</body>
</html>`;

  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.print();
  }
}

/**
 * Main export handler - routes to appropriate export function
 */
export async function handleExport(format: ExportFormat, options: ExportOptions): Promise<void> {
  const { query } = options;
  const safeFilename = query.slice(0, 50).replace(/[^a-zA-Z0-9]/g, '_');
  const timestamp = new Date().toISOString().split('T')[0];

  switch (format) {
    case 'pdf':
      generatePDFPrint(options);
      break;

    case 'csv':
      const csv = generateCSV(options);
      downloadFile(csv, `vettis_report_${safeFilename}_${timestamp}.csv`, 'text/csv');
      break;

    case 'json':
      const json = generateJSON(options);
      downloadFile(json, `vettis_audit_${safeFilename}_${timestamp}.json`, 'application/json');
      break;

    case 'markdown':
      const md = generateMarkdown(options);
      await copyToClipboard(md);
      break;

    default:
      console.warn(`Unknown export format: ${format}`);
  }
}

/**
 * Financial Deep Dive export handler
 */
export async function handleFinancialExport(format: ExportFormat, options: FinancialExportOptions): Promise<void> {
  const { ticker } = options;
  const timestamp = new Date().toISOString().split('T')[0];

  switch (format) {
    case 'pdf':
      generateFinancialPDFPrint(options);
      break;

    case 'csv':
      const csv = generateFinancialCSV(options);
      downloadFile(csv, `vettis_financial_${ticker}_${timestamp}.csv`, 'text/csv');
      break;

    case 'json':
      const json = generateFinancialJSON(options);
      downloadFile(json, `vettis_financial_audit_${ticker}_${timestamp}.json`, 'application/json');
      break;

    case 'markdown':
      const md = generateFinancialMarkdown(options);
      await copyToClipboard(md);
      break;

    default:
      console.warn(`Unknown export format: ${format}`);
  }
}

// ============================================================================
// STRATEGIC BLUEPRINT EXPORT FUNCTIONS
// ============================================================================

/** CSV-safe string: escape quotes and replace newlines with spaces */
function csvSafe(value: string | undefined | null): string {
  return (value || '').replace(/"/g, '""').replace(/\n/g, ' ');
}

/**
 * Generate Markdown for Strategic Blueprint (full audit trail)
 */
export function generateStrategicMarkdown(options: StrategicExportOptions): string {
  const { query, topic, intensity, workstreams, conflicts, stressTestResults, debateArguments, debateRounds, mediatorConsensus, ghostDeck, deliverable, competitorData, startedAt, completedAt, timestamp = new Date().toISOString() } = options;
  const ks = deliverable?.killSheet;

  let md = `# Strategic Analysis: ${topic}\n\n`;
  md += `**Query:** ${query}\n`;
  md += `**Generated:** ${new Date(timestamp).toLocaleString()}\n`;
  md += `**Intensity:** ${intensity}\n`;
  md += `**Workstreams:** ${workstreams.length}\n`;
  md += `**Debate Rounds:** ${debateRounds}\n`;
  if (startedAt && completedAt) {
    const durationSec = (new Date(completedAt).getTime() - new Date(startedAt).getTime()) / 1000;
    md += `**Duration:** ${durationSec.toFixed(1)}s\n`;
  }
  if (deliverable?.totalLatencySeconds) {
    md += `**Total Latency:** ${deliverable.totalLatencySeconds.toFixed(1)}s\n`;
  }
  md += '\n';

  // Executive Summary & Recommendation
  if (ks?.executiveSummary || deliverable?.executiveSummary) {
    md += `## Executive Summary\n\n${ks?.executiveSummary || deliverable?.executiveSummary}\n\n`;
  }
  if (ks?.recommendation) {
    md += `## Strategic Recommendation\n\n${ks.recommendation}\n\n`;
    if (ks.confidenceLevel) {
      md += `**Confidence:** ${ks.confidenceLevel}\n\n`;
    }
  }

  // Key Findings
  if (ks?.keyFindings && ks.keyFindings.length > 0) {
    md += `## Key Findings\n\n`;
    ks.keyFindings.forEach((f, i) => { md += `${i + 1}. ${f}\n`; });
    md += '\n';
  }

  // Risks & Opportunities
  if (ks?.risks && ks.risks.length > 0) {
    md += `## Risks (Headwinds)\n\n`;
    ks.risks.forEach(r => { md += `- ${r}\n`; });
    md += '\n';
  }
  if (ks?.opportunities && ks.opportunities.length > 0) {
    md += `## Opportunities (Tailwinds)\n\n`;
    ks.opportunities.forEach(o => { md += `- ${o}\n`; });
    md += '\n';
  }

  // Next Steps
  if (ks?.nextSteps && ks.nextSteps.length > 0) {
    md += `## Recommended Next Steps\n\n`;
    ks.nextSteps.forEach((s, i) => { md += `${i + 1}. ${s}\n`; });
    md += '\n';
  }

  // Workstreams
  if (workstreams.length > 0) {
    md += `## Workstreams (${workstreams.length})\n\n`;
    workstreams.forEach((ws, wIdx) => {
      md += `### ${wIdx + 1}. ${ws.name}\n\n`;
      if (ws.keyQuestions.length > 0) {
        md += `**Key Questions:** ${ws.keyQuestions.join('; ')}\n\n`;
      }
      if (ws.keyFindings.length > 0) {
        md += `**Key Findings:**\n`;
        ws.keyFindings.forEach(f => { md += `- ${f}\n`; });
        md += '\n';
      }
      if (ws.synthesisPreview) {
        md += `**Synthesis:** ${ws.synthesisPreview}\n\n`;
      }
      // Agents
      ws.agents.forEach((agent, aIdx) => {
        md += `#### Agent ${aIdx + 1}: ${agent.agentRole} (${agent.model})\n\n`;
        if (agent.latencySeconds) {
          md += `*Latency: ${agent.latencySeconds.toFixed(1)}s*\n\n`;
        }
        if (agent.fullOutput) {
          md += `${agent.fullOutput}\n\n`;
        } else if (agent.outputPreview) {
          md += `${agent.outputPreview}\n\n`;
        }
        if (agent.keyFindings && agent.keyFindings.length > 0) {
          md += `**Agent Key Findings:**\n`;
          agent.keyFindings.forEach(f => { md += `- ${f}\n`; });
          md += '\n';
        }
        if (agent.sources && agent.sources.length > 0) {
          md += `**Sources (${agent.sources.length}):**\n`;
          agent.sources.forEach(s => {
            md += s.url ? `- [${s.title}](${s.url})\n` : `- ${s.title}\n`;
          });
          md += '\n';
        }
      });
    });
  }

  // Conflicts
  if (conflicts.length > 0) {
    md += `## Cross-Workstream Conflicts (${conflicts.length})\n\n`;
    conflicts.forEach((c, i) => {
      md += `### Conflict ${i + 1}: ${c.description}\n\n`;
      md += `- **Severity:** ${c.severity}\n`;
      md += `- **Workstream A (${c.workstreamA}):** ${c.findingA}\n`;
      md += `- **Workstream B (${c.workstreamB}):** ${c.findingB}\n`;
      if (c.resolved && c.resolution) {
        md += `- **Resolution:** ${c.resolution}\n`;
        if (c.resolutionRationale) {
          md += `- **Rationale:** ${c.resolutionRationale}\n`;
        }
      } else {
        md += `- **Status:** Unresolved\n`;
      }
      md += '\n';
    });
  }

  // Stress Test Results
  if (stressTestResults.length > 0) {
    md += `## Stress Test Results (${stressTestResults.length})\n\n`;
    stressTestResults.forEach((st, i) => {
      const statusLabel = st.testResult === 'validated' ? 'Validated' : st.testResult === 'invalidated' ? 'Challenged' : 'Inconclusive';
      md += `### ${i + 1}. ${st.assumptionStatement}\n\n`;
      md += `**Result:** ${statusLabel} (${Math.round(st.confidence * 100)}% confidence)\n\n`;
      if (st.evidenceFor.length > 0) {
        md += `**Supporting Evidence:**\n`;
        st.evidenceFor.forEach(e => { md += `- ${e}\n`; });
        md += '\n';
      }
      if (st.evidenceAgainst.length > 0) {
        md += `**Challenging Evidence:**\n`;
        st.evidenceAgainst.forEach(e => { md += `- ${e}\n`; });
        md += '\n';
      }
      if (st.critique) {
        md += `**Critique:** ${st.critique}\n\n`;
      }
    });
  }

  // Debate Arguments
  if (debateArguments.length > 0) {
    md += `## AI Council Debate (${debateRounds} rounds)\n\n`;
    const rounds = new Map<number, DebateArgument[]>();
    debateArguments.forEach(arg => {
      const existing = rounds.get(arg.roundNumber) || [];
      existing.push(arg);
      rounds.set(arg.roundNumber, existing);
    });
    rounds.forEach((args, roundNum) => {
      md += `### Round ${roundNum}\n\n`;
      args.forEach(arg => {
        md += `**${arg.debaterName}** (${arg.debaterRole}) — *${arg.position}* (${Math.round(arg.confidence * 100)}%)\n\n`;
        md += `${arg.argument}\n\n`;
        if (arg.counterPoints.length > 0) {
          md += `Counter-points: ${arg.counterPoints.join('; ')}\n\n`;
        }
      });
    });
  }

  // Mediator Consensus
  if (mediatorConsensus) {
    md += `## Mediator Consensus\n\n`;
    md += `**Consensus Reached:** ${mediatorConsensus.consensusReached ? 'Yes' : 'No'}\n`;
    md += `**Confidence:** ${Math.round(mediatorConsensus.confidence * 100)}%\n`;
    if (mediatorConsensus.winningPosition) {
      md += `**Winning Position:** ${mediatorConsensus.winningPosition}\n`;
    }
    md += '\n';
    if (mediatorConsensus.keyAgreements.length > 0) {
      md += `**Key Agreements:**\n`;
      mediatorConsensus.keyAgreements.forEach(a => { md += `- ${a}\n`; });
      md += '\n';
    }
    if (mediatorConsensus.unresolvedTensions.length > 0) {
      md += `**Unresolved Tensions:**\n`;
      mediatorConsensus.unresolvedTensions.forEach(t => { md += `- ${t}\n`; });
      md += '\n';
    }
    if (mediatorConsensus.finalRecommendation) {
      md += `**Final Recommendation:** ${mediatorConsensus.finalRecommendation}\n\n`;
    }
    if (mediatorConsensus.rationale) {
      md += `**Rationale:** ${mediatorConsensus.rationale}\n\n`;
    }
  }

  // Ghost Deck
  if (ghostDeck.length > 0) {
    md += `## Ghost Deck (${ghostDeck.length} slides)\n\n`;
    ghostDeck.forEach(slide => {
      md += `### Slide ${slide.slideNumber}: ${slide.title}\n\n`;
      if (slide.headlineInsight) {
        md += `*${slide.headlineInsight}*\n\n`;
      }
      if (slide.keyPoints.length > 0) {
        md += `**Key Points:**\n`;
        slide.keyPoints.forEach(p => { md += `- ${p}\n`; });
        md += '\n';
      }
      if (slide.supportingEvidence.length > 0) {
        md += `**Evidence:**\n`;
        slide.supportingEvidence.forEach(e => { md += `- ${e}\n`; });
        md += '\n';
      }
    });
  }

  // Competitor Data
  if (competitorData.length > 0) {
    md += `## Competitor Landscape\n\n`;
    md += `| Company | Ticker | Price | Market Cap | P/E | Change |\n`;
    md += `|---------|--------|-------|------------|-----|--------|\n`;
    competitorData.forEach(c => {
      md += `| ${c.companyName} | ${c.ticker} | ${c.priceFormatted || c.price || 'N/A'} | ${c.marketCapFormatted || 'N/A'} | ${c.peFormatted || c.peRatio || 'N/A'} | ${c.changeFormatted || 'N/A'} |\n`;
    });
    md += '\n';
  }

  md += `\n---\n*Generated by Vettis AI Strategic Blueprint*\n`;
  return md;
}

/**
 * Generate CSV for Strategic Blueprint
 */
export function generateStrategicCSV(options: StrategicExportOptions): string {
  const { topic, intensity, workstreams, conflicts, stressTestResults, debateArguments, mediatorConsensus, competitorData, deliverable } = options;
  const ks = deliverable?.killSheet;

  let csv = '';

  // Summary
  csv += 'STRATEGIC ANALYSIS SUMMARY\n';
  csv += 'Topic,Intensity,Workstreams,Confidence,Recommendation\n';
  csv += `"${csvSafe(topic)}","${intensity}",${workstreams.length},"${csvSafe(ks?.confidenceLevel)}","${csvSafe(ks?.recommendation)}"\n\n`;

  // Kill Sheet
  if (ks) {
    csv += 'KILL SHEET\n';
    csv += 'Field,Value\n';
    csv += `"Executive Summary","${csvSafe(ks.executiveSummary)}"\n`;
    csv += `"Recommendation","${csvSafe(ks.recommendation)}"\n`;
    csv += `"Confidence","${csvSafe(ks.confidenceLevel)}"\n`;
    ks.keyFindings.forEach((f, i) => { csv += `"Key Finding ${i + 1}","${csvSafe(f)}"\n`; });
    ks.risks.forEach((r, i) => { csv += `"Risk ${i + 1}","${csvSafe(r)}"\n`; });
    ks.opportunities.forEach((o, i) => { csv += `"Opportunity ${i + 1}","${csvSafe(o)}"\n`; });
    ks.nextSteps.forEach((s, i) => { csv += `"Next Step ${i + 1}","${csvSafe(s)}"\n`; });
    csv += '\n';
  }

  // Workstream Agents
  csv += 'WORKSTREAM AGENTS\n';
  csv += 'Workstream,Agent Role,Model,Status,Latency (s),Citations,Output\n';
  workstreams.forEach(ws => {
    ws.agents.forEach(agent => {
      const output = csvSafe(agent.fullOutput || agent.outputPreview);
      csv += `"${csvSafe(ws.name)}","${csvSafe(agent.agentRole)}","${csvSafe(agent.model)}","${agent.status}",${agent.latencySeconds?.toFixed(1) || 'N/A'},${agent.citationCount},"${output}"\n`;
    });
  });
  csv += '\n';

  // Agent Sources
  const allSources: { workstream: string; agent: string; title: string; url: string; type: string }[] = [];
  workstreams.forEach(ws => {
    ws.agents.forEach(agent => {
      (agent.sources || []).forEach(s => {
        allSources.push({ workstream: ws.name, agent: agent.agentRole, title: s.title, url: s.url || '', type: s.type });
      });
    });
  });
  if (allSources.length > 0) {
    csv += 'AGENT SOURCES\n';
    csv += 'Workstream,Agent,Title,URL,Type\n';
    allSources.forEach(s => {
      csv += `"${csvSafe(s.workstream)}","${csvSafe(s.agent)}","${csvSafe(s.title)}","${csvSafe(s.url)}","${s.type}"\n`;
    });
    csv += '\n';
  }

  // Conflicts
  if (conflicts.length > 0) {
    csv += 'CROSS-WORKSTREAM CONFLICTS\n';
    csv += 'Severity,Workstream A,Finding A,Workstream B,Finding B,Description,Resolved,Resolution\n';
    conflicts.forEach(c => {
      csv += `"${c.severity}","${csvSafe(c.workstreamA)}","${csvSafe(c.findingA)}","${csvSafe(c.workstreamB)}","${csvSafe(c.findingB)}","${csvSafe(c.description)}",${c.resolved},"${csvSafe(c.resolution)}"\n`;
    });
    csv += '\n';
  }

  // Stress Tests
  if (stressTestResults.length > 0) {
    csv += 'STRESS TEST RESULTS\n';
    csv += 'Assumption,Result,Confidence,Evidence For,Evidence Against,Critique\n';
    stressTestResults.forEach(st => {
      csv += `"${csvSafe(st.assumptionStatement)}","${st.testResult}",${Math.round(st.confidence * 100)}%,"${csvSafe(st.evidenceFor.join('; '))}","${csvSafe(st.evidenceAgainst.join('; '))}","${csvSafe(st.critique)}"\n`;
    });
    csv += '\n';
  }

  // Debate Arguments
  if (debateArguments.length > 0) {
    csv += 'DEBATE ARGUMENTS\n';
    csv += 'Round,Debater,Role,Position,Confidence,Argument,Counter-Points\n';
    debateArguments.forEach(arg => {
      csv += `${arg.roundNumber},"${csvSafe(arg.debaterName)}","${csvSafe(arg.debaterRole)}","${csvSafe(arg.position)}",${Math.round(arg.confidence * 100)}%,"${csvSafe(arg.argument)}","${csvSafe(arg.counterPoints.join('; '))}"\n`;
    });
    csv += '\n';
  }

  // Mediator Consensus
  if (mediatorConsensus) {
    csv += 'MEDIATOR CONSENSUS\n';
    csv += 'Field,Value\n';
    csv += `"Consensus Reached","${mediatorConsensus.consensusReached}"\n`;
    csv += `"Confidence","${Math.round(mediatorConsensus.confidence * 100)}%"\n`;
    csv += `"Winning Position","${csvSafe(mediatorConsensus.winningPosition)}"\n`;
    csv += `"Final Recommendation","${csvSafe(mediatorConsensus.finalRecommendation)}"\n`;
    csv += `"Rationale","${csvSafe(mediatorConsensus.rationale)}"\n`;
    mediatorConsensus.keyAgreements.forEach((a, i) => { csv += `"Key Agreement ${i + 1}","${csvSafe(a)}"\n`; });
    mediatorConsensus.unresolvedTensions.forEach((t, i) => { csv += `"Unresolved Tension ${i + 1}","${csvSafe(t)}"\n`; });
    csv += '\n';
  }

  // Competitor Data
  if (competitorData.length > 0) {
    csv += 'COMPETITOR DATA\n';
    csv += 'Company,Ticker,Price,Market Cap,P/E,EPS,Change %,Sector,Industry\n';
    competitorData.forEach(c => {
      csv += `"${csvSafe(c.companyName)}","${c.ticker}","${c.priceFormatted || c.price || 'N/A'}","${c.marketCapFormatted || 'N/A'}","${c.peFormatted || c.peRatio || 'N/A'}","${c.epsFormatted || c.eps || 'N/A'}","${c.changeFormatted || 'N/A'}","${csvSafe(c.sector)}","${csvSafe(c.industry)}"\n`;
    });
    csv += '\n';
  }

  return csv;
}

/**
 * Generate JSON audit trail for Strategic Blueprint
 */
export function generateStrategicJSON(options: StrategicExportOptions): string {
  const { query, topic, intensity, workstreams, conflicts, stressTestResults, debateArguments, debateRounds, mediatorConsensus, ghostDeck, deliverable, competitorData, startedAt, completedAt, timestamp = new Date().toISOString() } = options;

  const auditTrail = {
    meta: {
      type: 'strategic_blueprint',
      version: '1.0',
      query,
      topic,
      intensity,
      generatedAt: timestamp,
      startedAt: startedAt || null,
      completedAt: completedAt || null,
      totalLatencySeconds: deliverable?.totalLatencySeconds || null,
      workstreamCount: workstreams.length,
      debateRounds,
    },
    deliverable: deliverable ? {
      killSheet: deliverable.killSheet || null,
      executiveSummary: deliverable.executiveSummary || null,
      totalLatencySeconds: deliverable.totalLatencySeconds || null,
    } : null,
    workstreams: workstreams.map(ws => ({
      id: ws.id,
      name: ws.name,
      status: ws.status,
      keyQuestions: ws.keyQuestions,
      keyFindings: ws.keyFindings,
      totalCitations: ws.totalCitations,
      latencySeconds: ws.latencySeconds,
      synthesisPreview: ws.synthesisPreview || null,
      agents: ws.agents.map(agent => ({
        agentRole: agent.agentRole,
        model: agent.model,
        status: agent.status,
        output: agent.fullOutput || agent.outputPreview || null,
        keyFindings: agent.keyFindings || [],
        sources: agent.sources || [],
        latencySeconds: agent.latencySeconds,
        citationCount: agent.citationCount,
      })),
    })),
    conflicts,
    stressTestResults,
    debateArguments,
    mediatorConsensus: mediatorConsensus || null,
    ghostDeck,
    competitorData,
  };

  return JSON.stringify(auditTrail, null, 2);
}

/**
 * Generate PDF (print) for Strategic Blueprint
 */
function generateStrategicPDFPrint(options: StrategicExportOptions): void {
  const { query, topic, intensity, workstreams, conflicts, stressTestResults, debateArguments, debateRounds, mediatorConsensus, ghostDeck, deliverable, competitorData, timestamp = new Date().toISOString() } = options;
  const ks = deliverable?.killSheet;

  const html = `
<!DOCTYPE html>
<html>
<head>
  <title>Strategic Analysis - ${topic}</title>
  <style>
    body { font-family: system-ui, sans-serif; max-width: 900px; margin: 0 auto; padding: 40px; color: #1a1a1a; }
    h1 { font-size: 28px; border-bottom: 3px solid #3b82f6; padding-bottom: 12px; }
    h2 { font-size: 18px; color: #444; margin-top: 32px; border-bottom: 1px solid #ddd; padding-bottom: 8px; }
    h3 { font-size: 14px; color: #666; }
    .meta { color: #666; font-size: 12px; margin-bottom: 24px; }
    .summary { background: #eff6ff; padding: 20px; border-radius: 8px; border-left: 4px solid #3b82f6; margin: 20px 0; }
    .recommendation { background: #f0fdf4; padding: 16px; border-radius: 8px; border-left: 4px solid #22c55e; margin: 16px 0; }
    .workstream { background: #fafafa; padding: 16px; border-radius: 8px; margin: 12px 0; border-left: 3px solid #3b82f6; }
    .agent { background: #f9f9f9; padding: 12px; border-radius: 6px; margin: 8px 0; border-left: 3px solid #888; }
    .agent-header { font-weight: 600; margin-bottom: 6px; }
    .conflict { background: #fef2f2; padding: 12px; border-radius: 6px; margin: 8px 0; }
    .stress-test { background: #fafafa; padding: 12px; border-radius: 6px; margin: 8px 0; }
    .validated { border-left: 3px solid #22c55e; }
    .invalidated { border-left: 3px solid #ef4444; }
    .inconclusive { border-left: 3px solid #f59e0b; }
    .debate { background: #faf5ff; padding: 12px; border-radius: 6px; margin: 8px 0; border-left: 3px solid #a855f7; }
    .mediator { background: #f0fdf4; padding: 16px; border-radius: 8px; border-left: 4px solid #22c55e; margin: 16px 0; }
    .list-item { margin: 4px 0; padding: 4px 0; }
    .risk { color: #dc2626; }
    .opportunity { color: #16a34a; }
    table { width: 100%; border-collapse: collapse; margin: 12px 0; font-size: 12px; }
    th, td { padding: 8px; border: 1px solid #ddd; text-align: left; }
    th { background: #f5f5f5; font-weight: 600; }
    .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; font-size: 11px; color: #999; }
    @media print { body { padding: 20px; } .workstream, .agent, .stress-test, .debate { page-break-inside: avoid; } }
  </style>
</head>
<body>
  <h1>Strategic Analysis: ${topic}</h1>
  <div class="meta">
    <strong>Query:</strong> ${query}<br>
    <strong>Generated:</strong> ${new Date(timestamp).toLocaleString()}<br>
    <strong>Intensity:</strong> ${intensity} |
    <strong>Workstreams:</strong> ${workstreams.length} |
    <strong>Debate Rounds:</strong> ${debateRounds}${deliverable?.totalLatencySeconds ? ` | <strong>Duration:</strong> ${deliverable.totalLatencySeconds.toFixed(1)}s` : ''}
  </div>

  ${(ks?.executiveSummary || deliverable?.executiveSummary) ? `
  <div class="summary">
    <h3 style="margin-top:0;color:#3b82f6">Executive Summary</h3>
    <p>${(ks?.executiveSummary || deliverable?.executiveSummary || '').replace(/\n/g, '<br>')}</p>
  </div>
  ` : ''}

  ${ks?.recommendation ? `
  <div class="recommendation">
    <h3 style="margin-top:0;color:#22c55e">Strategic Recommendation${ks.confidenceLevel ? ` (${ks.confidenceLevel})` : ''}</h3>
    <p>${ks.recommendation.replace(/\n/g, '<br>')}</p>
  </div>
  ` : ''}

  ${ks?.keyFindings && ks.keyFindings.length > 0 ? `
  <h2>Key Findings</h2>
  <ol>${ks.keyFindings.map(f => `<li>${f}</li>`).join('')}</ol>
  ` : ''}

  ${(ks?.risks && ks.risks.length > 0) || (ks?.opportunities && ks.opportunities.length > 0) ? `
  <h2>Risks & Opportunities</h2>
  <table>
    <tr><th class="risk">Risks (Headwinds)</th><th class="opportunity">Opportunities (Tailwinds)</th></tr>
    <tr>
      <td>${(ks?.risks || []).map(r => `<div class="list-item">- ${r}</div>`).join('') || 'None identified'}</td>
      <td>${(ks?.opportunities || []).map(o => `<div class="list-item">- ${o}</div>`).join('') || 'None identified'}</td>
    </tr>
  </table>
  ` : ''}

  ${ks?.nextSteps && ks.nextSteps.length > 0 ? `
  <h2>Recommended Next Steps</h2>
  <ol>${ks.nextSteps.map(s => `<li>${s}</li>`).join('')}</ol>
  ` : ''}

  ${workstreams.length > 0 ? `
  <h2>Workstreams</h2>
  ${workstreams.map((ws, i) => `
    <div class="workstream">
      <h3 style="margin-top:0">${i + 1}. ${ws.name}</h3>
      ${ws.keyQuestions.length > 0 ? `<p style="color:#666;font-size:12px"><strong>Key Questions:</strong> ${ws.keyQuestions.join('; ')}</p>` : ''}
      ${ws.keyFindings.length > 0 ? `<p><strong>Findings:</strong></p><ul>${ws.keyFindings.map(f => `<li>${f}</li>`).join('')}</ul>` : ''}
      ${ws.agents.map(agent => `
        <div class="agent">
          <div class="agent-header">${agent.agentRole} <span style="color:#888;font-weight:normal">(${agent.model})</span></div>
          <div style="font-size:13px">${(agent.fullOutput || agent.outputPreview || 'No output').replace(/\n/g, '<br>')}</div>
        </div>
      `).join('')}
    </div>
  `).join('')}
  ` : ''}

  ${conflicts.length > 0 ? `
  <h2>Cross-Workstream Conflicts (${conflicts.length})</h2>
  ${conflicts.map(c => `
    <div class="conflict">
      <strong>${c.severity.toUpperCase()}:</strong> ${c.description}<br>
      <small>${c.workstreamA}: ${c.findingA}</small><br>
      <small>${c.workstreamB}: ${c.findingB}</small>
      ${c.resolved ? `<br><small style="color:#22c55e"><strong>Resolved:</strong> ${c.resolution}</small>` : ''}
    </div>
  `).join('')}
  ` : ''}

  ${stressTestResults.length > 0 ? `
  <h2>Stress Test Results (${stressTestResults.length})</h2>
  ${stressTestResults.map(st => `
    <div class="stress-test ${st.testResult}">
      <strong>${st.assumptionStatement}</strong><br>
      <span style="color:${st.testResult === 'validated' ? '#22c55e' : st.testResult === 'invalidated' ? '#ef4444' : '#f59e0b'}">
        ${st.testResult === 'validated' ? 'Validated' : st.testResult === 'invalidated' ? 'Challenged' : 'Inconclusive'} (${Math.round(st.confidence * 100)}%)
      </span>
      ${st.evidenceFor.length > 0 ? `<br><small><strong>Supporting:</strong> ${st.evidenceFor.join('; ')}</small>` : ''}
      ${st.evidenceAgainst.length > 0 ? `<br><small><strong>Challenging:</strong> ${st.evidenceAgainst.join('; ')}</small>` : ''}
    </div>
  `).join('')}
  ` : ''}

  ${debateArguments.length > 0 ? `
  <h2>AI Council Debate (${debateRounds} rounds)</h2>
  ${debateArguments.map(arg => `
    <div class="debate">
      <strong>${arg.debaterName}</strong> (${arg.debaterRole}) — <em>${arg.position}</em> (${Math.round(arg.confidence * 100)}%)<br>
      <div style="font-size:13px;margin-top:6px">${arg.argument.replace(/\n/g, '<br>')}</div>
      ${arg.counterPoints.length > 0 ? `<small style="color:#666">Counter-points: ${arg.counterPoints.join('; ')}</small>` : ''}
    </div>
  `).join('')}
  ` : ''}

  ${mediatorConsensus ? `
  <div class="mediator">
    <h3 style="margin-top:0;color:#22c55e">Mediator Consensus (${Math.round(mediatorConsensus.confidence * 100)}% confidence)</h3>
    ${mediatorConsensus.winningPosition ? `<p><strong>Winning Position:</strong> ${mediatorConsensus.winningPosition}</p>` : ''}
    ${mediatorConsensus.finalRecommendation ? `<p>${mediatorConsensus.finalRecommendation}</p>` : ''}
    ${mediatorConsensus.keyAgreements.length > 0 ? `<p><strong>Agreements:</strong></p><ul>${mediatorConsensus.keyAgreements.map(a => `<li>${a}</li>`).join('')}</ul>` : ''}
    ${mediatorConsensus.unresolvedTensions.length > 0 ? `<p><strong>Unresolved:</strong></p><ul>${mediatorConsensus.unresolvedTensions.map(t => `<li>${t}</li>`).join('')}</ul>` : ''}
  </div>
  ` : ''}

  ${ghostDeck.length > 0 ? `
  <h2>Ghost Deck (${ghostDeck.length} slides)</h2>
  ${ghostDeck.map(slide => `
    <div class="workstream" style="border-left-color: #a855f7;">
      <h3 style="margin-top:0">Slide ${slide.slideNumber}: ${slide.title}</h3>
      ${slide.headlineInsight ? `<p style="font-style:italic;color:#666">${slide.headlineInsight}</p>` : ''}
      ${slide.keyPoints.length > 0 ? `<ul>${slide.keyPoints.map(p => `<li>${p}</li>`).join('')}</ul>` : ''}
    </div>
  `).join('')}
  ` : ''}

  ${competitorData.length > 0 ? `
  <h2>Competitor Landscape</h2>
  <table>
    <tr><th>Company</th><th>Ticker</th><th>Price</th><th>Market Cap</th><th>P/E</th><th>Change</th></tr>
    ${competitorData.map(c => `
      <tr>
        <td>${c.companyName}</td><td>${c.ticker}</td>
        <td>${c.priceFormatted || c.price || 'N/A'}</td>
        <td>${c.marketCapFormatted || 'N/A'}</td>
        <td>${c.peFormatted || c.peRatio || 'N/A'}</td>
        <td>${c.changeFormatted || 'N/A'}</td>
      </tr>
    `).join('')}
  </table>
  ` : ''}

  <div class="footer">Generated by Vettis AI Strategic Blueprint</div>
</body>
</html>`;

  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.print();
  }
}

/**
 * Strategic Blueprint export handler
 */
export async function handleStrategicExport(format: ExportFormat, options: StrategicExportOptions): Promise<void> {
  const safeTopic = options.topic.slice(0, 50).replace(/[^a-zA-Z0-9]/g, '_');
  const timestamp = new Date().toISOString().split('T')[0];

  switch (format) {
    case 'pdf':
      generateStrategicPDFPrint(options);
      break;

    case 'csv': {
      const csv = generateStrategicCSV(options);
      downloadFile(csv, `vettis_strategic_${safeTopic}_${timestamp}.csv`, 'text/csv');
      break;
    }

    case 'json': {
      const json = generateStrategicJSON(options);
      downloadFile(json, `vettis_strategic_audit_${safeTopic}_${timestamp}.json`, 'application/json');
      break;
    }

    case 'markdown': {
      const md = generateStrategicMarkdown(options);
      await copyToClipboard(md);
      break;
    }

    default:
      console.warn(`Unknown export format: ${format}`);
  }
}

